import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Loader2, Download, Send, Twitter, RefreshCw, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/hooks/useSettings";
import { withXHandle, xHandleFromUrl } from "@/lib/memeKit";
import { REMIX_STYLES, getStyle, renderRemix } from "@/lib/remixStyles";
import ShareToXDialog from "@/components/ShareToXDialog";

const MAX_MB = 15;

export default function RobbedRemix() {
  const { user } = useAuth();
  const { data: settings } = useSettings();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const targetBattleId = params.get("battle");
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const fileRef = useRef(null);
  const [hasImage, setHasImage] = useState(false);
  const [styleId, setStyleId] = useState("rugged");
  const [processing, setProcessing] = useState(false);
  const [o, setO] = useState({ ...getStyle("rugged").defaults, caption: "", branding: true, border: false, showLabel: true });
  // share
  const [shareOpen, setShareOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [canNative, setCanNative] = useState(false);
  const blobRef = useRef(null), fileBlobRef = useRef(null), textRef = useRef("");
  const track = (name, meta) => api.post("/analytics/event", { name, meta: meta || {} }).catch(() => {});
  useEffect(() => { track("remix_opened"); /* eslint-disable-next-line */ }, []);

  const draw = () => {
    if (!imgRef.current) return;
    setProcessing(true);
    requestAnimationFrame(() => {
      try { renderRemix(canvasRef.current, imgRef.current, getStyle(styleId), o, xHandleFromUrl(settings?.xUrl) || "@robbedcoin"); }
      catch (e) { toast.error("Couldn't process this image."); }
      setProcessing(false);
    });
  };
  useEffect(() => { if (hasImage) draw(); /* eslint-disable-next-line */ }, [styleId, o, hasImage]);

  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp|gif|bmp)$/i.test(f.type)) { toast.error("Please upload a PNG, JPG, WEBP or GIF image."); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Image too large (max ${MAX_MB}MB).`); return; }
    setProcessing(true);
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => { imgRef.current = img; setHasImage(true); setProcessing(false); URL.revokeObjectURL(url); track("remix_generated", { style: styleId }); };
    img.onerror = () => { toast.error("That file isn't a valid image."); setProcessing(false); URL.revokeObjectURL(url); };
    img.src = url;
  };

  const pickStyle = (id) => { track("remix_style_selected", { style: id }); setStyleId(id); setO((p) => ({ ...getStyle(id).defaults, caption: p.caption, branding: p.branding, border: p.border, showLabel: p.showLabel })); };
  const set = (k, v) => setO((p) => ({ ...p, [k]: v }));

  const getBlob = () => new Promise((r) => canvasRef.current.toBlob(r, "image/png"));

  const download = async () => {
    const b = await getBlob(); const url = URL.createObjectURL(b);
    const a = document.createElement("a"); a.href = url; a.download = "robbed-remix.png"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); toast.success("Downloaded."); track("remix_downloaded", { style: styleId });
  };
  const openShare = async () => {
    textRef.current = withXHandle(`${o.caption ? o.caption + "\n\n" : ""}Gave this meme the ROBBED treatment. $ROBBED · THEY GOT RICH. WE GOT ROBBED.`, settings?.xUrl);
    let p = null; try { p = canvasRef.current.toDataURL("image/png"); } catch {}
    setPreview(p);
    const b = await getBlob(); blobRef.current = b;
    const file = b ? new File([b], "robbed-remix.png", { type: "image/png" }) : null; fileBlobRef.current = file;
    setCanNative(!!(file && navigator.canShare && navigator.canShare({ files: [file] })));
    setShareOpen(true);
  };
  const doNativeShare = async () => {
    try { await navigator.share({ files: [fileBlobRef.current], text: textRef.current });
      api.post("/analytics/event", { name: "remix_share_x", meta: { method: "native", image: "attached", style: styleId } }).catch(() => {});
      setShareOpen(false);
    } catch (err) { if (err?.name === "AbortError") return; }
  };
  const onManual = () => api.post("/analytics/event", { name: "remix_share_x", meta: { image: "manual", style: styleId } }).catch(() => {});

  const postWall = async () => {
    if (!user) { toast.error("Log in to post."); navigate("/login"); return; }
    const b = await getBlob(); const fd = new FormData();
    fd.append("file", b, "remix.png"); fd.append("caption", o.caption || "ROBBED Remix"); fd.append("category", "meme");
    toast.loading("Posting to the Wall…", { id: "wall" });
    try { await api.post("/memes", fd); toast.success("Posted to the Wall (pending review).", { id: "wall" }); track("remix_posted_wall", { style: styleId }); navigate("/wall"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: "wall" }); }
  };
  const enterBattle = async () => {
    if (!user) { toast.error("Log in to enter."); navigate("/login"); return; }
    let active;
    try { const { data } = await api.get("/battles");
      if (targetBattleId) active = data.find((b) => b.id === targetBattleId && b.status === "active");
      if (!active) active = data.find((b) => b.status === "active");
    } catch { toast.error("Couldn't load battles."); return; }
    if (!active) { toast.error("No active battle right now."); navigate("/battles"); return; }
    const b = await getBlob(); const fd = new FormData();
    fd.append("file", b, "remix.png"); fd.append("caption", o.caption || "");
    toast.loading("Entering battle…", { id: "bt" });
    try { await api.post(`/battles/${active.id}/submit`, fd); toast.success(`Entered "${active.title}".`, { id: "bt" }); track("remix_entered_battle", { style: styleId }); navigate(`/battles/${active.id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: "bt" }); }
  };
  const another = () => { imgRef.current = null; setHasImage(false); fileRef.current?.click(); };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="remix-page">
      <span className="tag bg-neon text-black inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> ROBbed Remix</span>
      <h1 className="headline text-cream text-4xl sm:text-6xl mt-4">ROBbed <span className="text-neon">REMIX</span></h1>
      <p className="font-mono text-dim mt-3 text-sm">Take any meme. Give it the ROBBED treatment.</p>

      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/bmp" className="hidden" onChange={onFile} data-testid="remix-file" />

      {!hasImage ? (
        <button onClick={() => fileRef.current?.click()} className="comic-card w-full mt-8 p-12 flex flex-col items-center justify-center gap-3 hover:border-neon transition-colors" data-testid="remix-upload-btn">
          {processing ? <Loader2 className="w-10 h-10 text-neon animate-spin" /> : <Upload className="w-10 h-10 text-neon" />}
          <span className="font-display font-900 uppercase text-cream">REMIX YOUR MEME</span>
          <span className="font-mono text-[11px] text-dim">Upload an image (PNG / JPG / WEBP / GIF, max {MAX_MB}MB)</span>
        </button>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          {/* preview */}
          <div className="comic-card p-3 relative">
            <canvas ref={canvasRef} className="w-full h-auto border-3 border-black bg-ink" data-testid="remix-canvas" />
            {processing && <div className="absolute inset-0 grid place-items-center bg-black/50" data-testid="remix-processing"><span className="btn-neon"><Loader2 className="w-4 h-4 animate-spin" /> Processing…</span></div>}
          </div>
          {/* controls */}
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="remix-styles">
              {REMIX_STYLES.map((s) => (
                <button key={s.id} onClick={() => pickStyle(s.id)} data-testid={`remix-style-${s.id}`}
                  className={`px-3 py-1.5 border-2 font-heading font-bold uppercase text-[10px] ${styleId === s.id ? "border-neon text-neon bg-neon/10" : "border-charcoal text-dim hover:text-cream"}`}>{s.name}</button>
              ))}
            </div>
            <p className="font-mono text-[11px] text-dim">{getStyle(styleId).tagline}</p>

            {[["brightness", 50, 150], ["contrast", 50, 200], ["saturation", 0, 200], ["glitch", 0, 1, 0.05]].map(([k, min, max, step]) => (
              <label key={k} className="block">
                <span className="font-mono text-[10px] uppercase text-dim">{k}: {o[k]}</span>
                <input type="range" min={min} max={max} step={step || 1} value={o[k]} onChange={(e) => set(k, parseFloat(e.target.value))} className="w-full accent-neon" data-testid={`remix-${k}`} />
              </label>
            ))}
            <input value={o.caption} onChange={(e) => set("caption", e.target.value)} maxLength={60} placeholder="Add a caption (optional)"
              className="w-full bg-ink border-3 border-black px-3 py-2 font-mono text-xs text-cream focus:outline-none focus:border-neon" data-testid="remix-caption" />
            <div className="flex flex-wrap gap-3 font-mono text-xs text-cream">
              <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={o.branding} onChange={(e) => set("branding", e.target.checked)} className="accent-neon w-4 h-4" data-testid="remix-branding" /> $ROBBED branding</label>
              <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={o.border} onChange={(e) => set("border", e.target.checked)} className="accent-neon w-4 h-4" data-testid="remix-border" /> Border</label>
              <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={o.showLabel} onChange={(e) => set("showLabel", e.target.checked)} className="accent-neon w-4 h-4" data-testid="remix-label" /> Style label</label>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <button onClick={openShare} className="btn-neon col-span-2" data-testid="remix-share"><Twitter className="w-4 h-4" /> Share To X</button>
              <button onClick={download} className="btn-ghost" data-testid="remix-download"><Download className="w-4 h-4" /> Download</button>
              <button onClick={postWall} className="btn-ghost !border-gold hover:!bg-gold" data-testid="remix-wall"><Send className="w-4 h-4" /> Post To Wall</button>
              <button onClick={enterBattle} className="btn-ghost !border-grape hover:!bg-grape hover:!text-cream" data-testid="remix-battle"><Send className="w-4 h-4" /> Enter Battle</button>
              <button onClick={another} className="btn-ghost" data-testid="remix-another"><RefreshCw className="w-4 h-4" /> Create Another</button>
            </div>
            <p className="font-mono text-[10px] text-dim">100% local · $0 AI. Not an official statement from the project.</p>
          </div>
        </div>
      )}

      <ShareToXDialog open={shareOpen} onOpenChange={setShareOpen} preview={preview} canNative={canNative}
        blob={blobRef.current} filename="robbed-remix.png" text={textRef.current}
        onNativeShare={doNativeShare} onManualInitiated={onManual} testidPrefix="remix-x" title="Share Remix on X" />
    </div>
  );
}
